/**
 * The identity provider's answer when it refuses a sign-in: it redirects back
 * with `?error=...&error_description=...&state=...` instead of `?code=...`
 * (RFC 6749, section 4.1.2.1). The ID server does that, for example, for an
 * account that is not a member of this tenant:
 * `error=access_denied&error_description=Tenant access denied: membership_missing.`
 */
export interface OAuthErrorRedirect {
  error: string;
  description: string | null;
}

/** Query parameters of an authorization response that must not stay in the address bar. */
const AUTHORIZATION_RESPONSE_PARAMETERS = ['error', 'error_description', 'error_uri', 'state', 'session_state', 'iss'];

/** The ID server's description for an account that is not a member of the tenant. */
const MEMBERSHIP_MISSING_MARKER = 'membership_missing';

/**
 * Reads an error redirect from the identity provider out of a query string.
 * A bare `error` parameter is not enough: an authorization response always
 * carries `state` (or at least `error_description`/`iss`), so an unrelated
 * `?error=` in an app link is left alone.
 */
export function readOAuthErrorRedirect(search: string): OAuthErrorRedirect | null {
  const params = new URLSearchParams(search);
  const error = params.get('error');
  if (!error) {
    return null;
  }
  if (!params.has('state') && !params.has('error_description') && !params.has('iss')) {
    return null;
  }
  const description = params.get('error_description');
  return { error, description: description ? description : null };
}

/** True for the ID server's "your account is not a member of this workspace" answer. */
export function isMembershipMissing(redirect: OAuthErrorRedirect): boolean {
  return redirect.error === 'access_denied' && (redirect.description ?? '').includes(MEMBERSHIP_MISSING_MARKER);
}

/** Drops the authorization response parameters from a URL, keeping everything else. */
export function removeOAuthErrorParameters(href: string): string {
  const url = new URL(href);
  let changed = false;
  for (const name of AUTHORIZATION_RESPONSE_PARAMETERS) {
    if (url.searchParams.has(name)) {
      url.searchParams.delete(name);
      changed = true;
    }
  }
  return changed ? url.toString() : href;
}

/**
 * The ID server's end-session endpoint, for when the discovery document did
 * not name one (OpenIddict serves it at /connect/logout).
 */
export function fallbackEndSessionUrl(issuer: string): string {
  return new URL('connect/logout', issuer.endsWith('/') ? issuer : `${issuer}/`).toString();
}
